import { useState, useEffect, useCallback, useRef } from "react";

enum ConnectionQuality {
  VerySlow = "Very Slow Connection",
  Slow = "Slow Connection",
  Good = "Good Connection",
  Error = "Error in speed test",
  Offline = "Offline",
  Checking = "Checking...",
}

interface InternetSpeed {
  networkStatus: ConnectionQuality;
  downloadSpeed: string | null;
  isOnline: boolean;
  isOnlineEventTriggered: boolean;
  refreshNetworkSpeed: () => void;
}

interface NetworkInformationLike {
  downlink?: number;
  effectiveType?: string;
  addEventListener?: (type: "change", listener: () => void) => void;
  removeEventListener?: (type: "change", listener: () => void) => void;
}

interface SpeedProbeResult {
  speedMbps: number | null;
}

const getConnectionQuality = (
  downlink?: number,
  effectiveType?: string,
): ConnectionQuality => {
  if (effectiveType === "slow-2g" || effectiveType === "2g") {
    return ConnectionQuality.VerySlow;
  }

  if (effectiveType === "3g") {
    return ConnectionQuality.Slow;
  }

  if (typeof downlink === "number") {
    if (downlink < 1) return ConnectionQuality.VerySlow;
    if (downlink < 5) return ConnectionQuality.Slow;
    return ConnectionQuality.Good;
  }

  if (effectiveType === "4g") {
    return ConnectionQuality.Good;
  }

  return ConnectionQuality.Good;
};

const getConnectionQualityFromMbps = (
  speedInMbps: number,
  effectiveType?: string,
  lowSpeedStreak = 0,
): ConnectionQuality => {
  // Avoid over-penalizing fast-network types from noisy small probes.
  if (effectiveType === "4g") {
    if (speedInMbps < 0.6) return ConnectionQuality.Slow;
    return ConnectionQuality.Good;
  }

  // Mark "Very Slow" only after persistent very low readings.
  if (speedInMbps < 0.25) {
    return lowSpeedStreak >= 2
      ? ConnectionQuality.VerySlow
      : ConnectionQuality.Slow;
  }
  if (speedInMbps < 0.6) return ConnectionQuality.Slow;
  return ConnectionQuality.Good;
};

const useInternetSpeedV2 = (): InternetSpeed => {
  const [networkStatus, setNetworkStatus] = useState<ConnectionQuality>(
    ConnectionQuality.Checking,
  );
  const [downloadSpeed, setDownloadSpeed] = useState<string | null>(null);
  const [isOnline, setIsOnline] = useState<boolean>(true);
  const [isOnlineEventTriggered, setIsOnlineEventTriggered] =
    useState<boolean>(false);
  const lowSpeedStreakRef = useRef<number>(0);
  const measureSpeedFromUrl = useCallback(
    async (probeUrl: string): Promise<SpeedProbeResult> => {
      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), 5000);
      try {
        const startTime = performance.now();
        const cacheBuster = Date.now();
        const traceJoiner = probeUrl.includes("?") ? "&" : "?";
        const response = await fetch(
          `${probeUrl}${traceJoiner}trace=${cacheBuster}`,
          {
            cache: "no-store",
            signal: controller.signal,
          },
        );

        if (!response.ok) return { speedMbps: null };

        const blob = await response.blob();
        if (blob.size <= 0) return { speedMbps: null };

        const endTime = performance.now();
        const durationInSeconds = (endTime - startTime) / 1000;
        if (durationInSeconds <= 0) return { speedMbps: null };

        const sizeInBits = blob.size * 8;
        const speedInMbps = sizeInBits / (durationInSeconds * 1_000_000);
        return {
          speedMbps: Number.isFinite(speedInMbps) ? speedInMbps : null,
        };
      } catch (error) {
        return { speedMbps: null };
      } finally {
        window.clearTimeout(timeoutId);
      }
    },
    [],
  );

  const measureRealtimeSpeedMbps = useCallback(async (): Promise<
    number | null
  > => {
    try {
      // Probe using local favicon only.
      const faviconProbe = await measureSpeedFromUrl("/favicon.ico");
      if (faviconProbe.speedMbps !== null) {
        return faviconProbe.speedMbps;
      }
      return null;
    } catch (error) {
      return null;
    }
  }, [measureSpeedFromUrl]);

  const getNavigatorConnection = useCallback(():
    | NetworkInformationLike
    | undefined => {
    if (typeof window === "undefined") return undefined;
    const nav = navigator as Navigator & {
      connection?: NetworkInformationLike;
      mozConnection?: NetworkInformationLike;
      webkitConnection?: NetworkInformationLike;
    };
    return nav.connection || nav.mozConnection || nav.webkitConnection;
  }, []);

  const updateConnectionDetails = useCallback(() => {
    const updateAsync = async () => {
      const onlineStatus =
        typeof navigator !== "undefined" ? navigator.onLine : true;
      setIsOnline(onlineStatus);

      if (!onlineStatus) {
        setNetworkStatus(ConnectionQuality.Offline);
        setDownloadSpeed(null);
        lowSpeedStreakRef.current = 0;
        return;
      }

      const connection = getNavigatorConnection();
      const downlink = connection?.downlink;
      const effectiveType = connection?.effectiveType;

      const measuredSpeedInMbps = await measureRealtimeSpeedMbps();
      if (measuredSpeedInMbps !== null) {
        if (measuredSpeedInMbps < 0.25) {
          lowSpeedStreakRef.current += 1;
        } else {
          lowSpeedStreakRef.current = 0;
        }
        setNetworkStatus(
          getConnectionQualityFromMbps(
            measuredSpeedInMbps,
            effectiveType,
            lowSpeedStreakRef.current,
          ),
        );
        setDownloadSpeed(measuredSpeedInMbps.toFixed(3));
        return;
      }

      // Existing browser-network-info logic kept intentionally as fallback.
      setNetworkStatus(getConnectionQuality(downlink, effectiveType));
      setDownloadSpeed(
        typeof downlink === "number" ? downlink.toFixed(2) : null,
      );
    };

    void updateAsync();
  }, [getNavigatorConnection, measureRealtimeSpeedMbps]);

  useEffect(() => {
    const handleOnlineStatus = () => {
      setIsOnlineEventTriggered(true);
      setIsOnline(true);
      updateConnectionDetails();
    };

    const handleOfflineStatus = () => {
      setIsOnline(false);
      setNetworkStatus(ConnectionQuality.Offline);
      setDownloadSpeed(null);
    };

    window.addEventListener("online", handleOnlineStatus);
    window.addEventListener("offline", handleOfflineStatus);

    const connection = getNavigatorConnection();
    connection?.addEventListener?.("change", updateConnectionDetails);
    updateConnectionDetails();

    return () => {
      window.removeEventListener("online", handleOnlineStatus);
      window.removeEventListener("offline", handleOfflineStatus);
      connection?.removeEventListener?.("change", updateConnectionDetails);
    };
  }, [getNavigatorConnection, updateConnectionDetails]);

  useEffect(() => {
    if (!isOnline) {
      setNetworkStatus(ConnectionQuality.Offline);
      setDownloadSpeed(null);
      lowSpeedStreakRef.current = 0;
    }
  }, [isOnline]);

  return {
    networkStatus,
    downloadSpeed,
    isOnline,
    isOnlineEventTriggered,
    refreshNetworkSpeed: updateConnectionDetails,
  };
};

export default useInternetSpeedV2;
